import React, { useEffect, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { AnchorSpan, ContainerDiv } from '../styledComponent/StyledComponent';
import dayjs from 'dayjs';
import { FormField, Label, Select, Option, SingleDatePicker, Toggle, Button, TextInput, BaseTable, Modal, SinglePickerValueType, TextTypes } from '@sede-enterprise/shell-ds-react-framework';
import { Col, Row } from '../flexbox/Flex';
import { Add, Search } from '@sede-enterprise/shell-ds-react-framework/build/esm/components/Icon/components';
import { postData } from '../../services/ApiService';
import { ApiUrls } from '../../helpers/ApiUrls';
import { DefaultOptionType } from 'rc-select/lib/Select';
import ResponseErrorModal from '../responseErrorModal/ResponseErrorModal';
import Loader from '../loader/Loader';
import { useToast } from 'rc-toastr';
import { APIErrorResponseInterface } from '../../helpers/InterfacesConstants';
import StatusCell from '../common/StatusCell';

interface AirportCustomerInterface  {
    id?:string ;
    country : string;
    customerName : string;
    customerCode: string;
    airport: string;
    startDate : SinglePickerValueType | null;
    endDate : SinglePickerValueType | null;
    isActive : boolean;
    isLocal : boolean;
}

const AirportCustomers = (props: any) => {

    const [openModal, setOpenModal] = useState(false);
    const [modelTitle, setModelTitle] = useState('');

    const [searchText , setSearchText] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isOnLoad, setIsOnLoad] = useState(true);
    const [miAirportCustomers, setMiAirportCustomers] = useState<AirportCustomerInterface[]>([]);

    const [apiErrorResponse, setApiErrorResponse] = useState<APIErrorResponseInterface | null>();
    const [airportCustomerDetails, setAirportCustomerDetails] = useState<AirportCustomerInterface>({
        id: '',
        country: '',
        customerName : '',
        customerCode: '',
        airport: '',
        startDate : null,
        endDate : null,
        isActive : false,
        isLocal : false,
    });
    const { toast } = useToast();
    const [countriesList, setCountriesList ] = useState<{countryId: string, countryName: string, airports:{airportId: number, airportName:string}[]}[]>([]);
    const [customersList, setCustomersList ] = useState<{customerId: string, customerName: string, customerCodes:string[]}[]>([]);
    const [customerCodesList, setCustomerCodesList ] = useState<string[]>([]);
    const [airportList, setAirportList ] = useState<{airportId: number, airportName: string}[]>([]);
    const columns: ColumnDef<AirportCustomerInterface>[] = [{
        header: 'Customer Name',
        accessorKey: 'customerName',
        size: 60
    },{
        header: 'Customer Code',
        accessorKey: 'customerCode',
        size: 60
    },{
        header: 'Airport Site',
        accessorKey: 'airport',
        size: 60
    },{
        header: 'Start Date',
        accessorKey: 'startDate',
        cell: ({row}) =>{
            return(
                dayjs(row.original.startDate).format('DD MMM YYYY')
            )
        },
        size: 60
    }, {
        header: 'End Date',
        accessorKey: 'endDate',
        cell: ({row}) =>{
            return(
                dayjs(row.original.endDate).format('DD MMM YYYY')
            )
        },
        size: 60
    }, {
        header: 'Status',
        accessorKey: 'isActive',
        cell: ({row}) =>{
            const isActive = row.original.isActive;
            return(
                <StatusCell status={isActive}/>
            )
        },
        size: 60
    }, {
        header: 'Local Customer',
        accessorKey: 'isLocal',
        cell: ({row}) =>{
            const isLocal = row.original.isLocal;
            return(
                isLocal ? 'True' : 'False'
            )
        },
        size: 60
    }, {
        header: 'Action',
        accessorKey: 'id',
        size: 60,
        cell: ({row}) =>{
            return(
                <AnchorSpan onClick={()=>{handleEditClick(row.original)}} key={row.id} style={{float: 'right', justifyContent: 'end', minWidth: '35px'}}>Edit</AnchorSpan>
            )
        },
    }];

    const handleEditClick = (row: AirportCustomerInterface) => {
        setModelTitle('Edit MI Airport Customer');
        setAirportCustomerDetails({ ...row, startDate: dayjs(row.startDate), endDate: dayjs(row.endDate)});
        setOpenModal(true);
    }

    const handleAddClick = () => {
        setModelTitle('Add MI Airport Customer');
        setCountriesList(props.getcountrylst());
        setCustomersList(props.getCustomerList());
        setAirportCustomerDetails({ 
            country: '',
            customerName : '',
            customerCode: '',
            airport: '',
            startDate : null,
            endDate : null,
            isActive : true,
            isLocal : false
        });
        setOpenModal(true);
    }

    const save = () => {
        const obj = {
            ...airportCustomerDetails,
            startDate: dayjs(airportCustomerDetails.startDate).format('YYYY-MM-DD'),
            endDate: dayjs(airportCustomerDetails.endDate).format('YYYY-MM-DD')
        }
        postData(ApiUrls.addorupdateairportcustomer, obj).then((response) => {
            if(response.status == 200){
                getMiAirportCustomers("");
                toast.success("Added Airport Customer to MI List");
                setOpenModal(false);
            }
        }).catch((error) => {
            setApiErrorResponse({
                message : error.response.data.message,
                traceId : error.response.data.traceId
            });
        });
    }

    const getMiAirportCustomers = (search: string) =>{
        setIsLoading(true);
        postData(ApiUrls.miAirportCustomers, {"searchTerm": search}).then((response) => {
            setIsLoading(false);
            setMiAirportCustomers(response.data);
        }).catch((error) => {
            setIsLoading(false);
            setApiErrorResponse({
                message : error.response.data.message,
                traceId : error.response.data.traceId
            });
        })
    }

    useEffect(() => {
        if(isOnLoad){
            setIsOnLoad(false);
            getMiAirportCustomers(searchText);
        }
    },[searchText]);

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            getMiAirportCustomers(searchText);
        }
    };


    const onCountryChange = (value: string, option : DefaultOptionType | DefaultOptionType[] ) => {
        if (Array.isArray(option)) {
            option.forEach((opt) => {
                if(opt['data-airports']){
                    setAirportList(opt['data-airports']);
                }
            });
        } else if(option && option['data-airports']){
            setAirportList(option['data-airports']);
        }else{
            setAirportList([]);
        }
        setAirportCustomerDetails({...airportCustomerDetails, country: value, airport:''});
    }

    const onCustomeChange = (value: string, option : DefaultOptionType | DefaultOptionType[] ) => {
        if (Array.isArray(option)) {
            option.forEach((opt) => {
                if(opt['data-customercodes']){
                    setCustomerCodesList(opt['data-customercodes']);
                }
            });
        } else if(option?.['data-customercodes']){
            setCustomerCodesList(option['data-customercodes']);
        }else{
            setCustomerCodesList([]);
        }
        setAirportCustomerDetails({...airportCustomerDetails, customerName: value, customerCode: ''});
    }
    
    return (
        <>
            <ContainerDiv>
                <Row>
					<Col size='65'>
						<Button onClick={()=>{handleAddClick()}}><Add/>Add MI Airport Customer</Button>
					</Col>
                    <Col size='35'>
                        <Row>
                            <Label bold style={{paddingRight:'4px'}} type={TextTypes.Label} htmlFor='search'>Search</Label>
                            <TextInput size="medium" onChange={(event)=>{setSearchText(event.target.value);}} suffix={{node: <Search />}} placeholder="Search Airport or Customer Name" id="search"  onKeyDown={handleKeyDown}/>
                        </Row>
					</Col>
                </Row>
            </ContainerDiv>
            
            <Row>
                <Col size="100">
                    { !isLoading && <BaseTable data={miAirportCustomers} columns={columns} noFooter/>}
                    { isLoading && <Loader/>}
                </Col>
            </Row>

            <Modal  open={openModal} onClose={() => {
                    setOpenModal(false);
                }} 

                actions={[{
                    label: 'Save',
                    action: () => {
                        save()
                    }
                }]}
                title={modelTitle}
                
                >
                <>
                    <FormField id="country" label="Country">
                        <Select value={airportCustomerDetails.country} onChange={(value, option) =>{onCountryChange(value, option)}}>
                            {countriesList.map((country)=>(
                                <Option value={country.countryName} key={country.countryId} data-airports={country.airports}>{country.countryName}</Option>
                            ))}
                        </Select>
                    </FormField>

                    <FormField id="airportSite" label="Airport Site">
                        <Select value={airportCustomerDetails?.airport} onChange={(value) =>{setAirportCustomerDetails({...airportCustomerDetails, airport: value})}}>
                            {airportList.map((airport)=>(
                                <Option value={airport.airportName} key={airport.airportId}>{airport.airportName}</Option>
                            ))}
                        </Select>
                    </FormField>

                    <FormField id="customerName" label="Customer">
                        <Select value={airportCustomerDetails.customerName} onChange={(value, option) =>{onCustomeChange(value, option)}}>
                            {customersList.map((customer)=>(

                                <Option value={customer.customerId.toString()} key={customer.customerId} data-customercodes={customer.customerCodes}>{customer.customerName}_{customer.customerId} </Option>

                            ))}
                        </Select>
                    </FormField>

                    <FormField id="customerCode" label="Customer Code">
                        <Select value={airportCustomerDetails.customerCode} onChange={(value) =>{setAirportCustomerDetails({...airportCustomerDetails, customerCode: value})}}>
                            {customerCodesList.map((customerCode)=>(
                                <Option key={customerCode} value={customerCode}>{customerCode}</Option>
                            ))}
                        </Select>
                    </FormField>

                    <FormField id="startDate" label="Start Date">
                        <SingleDatePicker value={airportCustomerDetails.startDate} onChange={(value) =>{setAirportCustomerDetails({...airportCustomerDetails, startDate: value})}} size='small' showToday={false}/>
                    </FormField>

                    <FormField id="endDate" label="End Date">
                        <SingleDatePicker value={airportCustomerDetails.endDate} onChange={(value) =>{setAirportCustomerDetails({...airportCustomerDetails, endDate: value})}} size='small' showToday={false}/>
                    </FormField>

                    <div style={{marginBottom:'10px'}}>
                    <Row>
                        <Col size="90"><Label bold className='statusLabel'>Status</Label></Col>
                        <Col size="10"><Toggle checked={airportCustomerDetails.isActive} onChange={(value)=>{setAirportCustomerDetails({ ...airportCustomerDetails, isActive: value})}}  /></Col>
                    </Row>
                    </div>

                    <Row>
                        <Col size="90"><Label bold className='statusLabel'>Local Customer</Label></Col>
                        <Col size="10"><Toggle checked={airportCustomerDetails.isLocal} onChange={(value)=>{setAirportCustomerDetails({ ...airportCustomerDetails, isLocal: value})}}  /></Col>
                    </Row>
                </>
            </Modal>
            <ResponseErrorModal errorDetails={apiErrorResponse} isOpen={apiErrorResponse ? true : false} onModalClose={()=>{setApiErrorResponse(null)}}></ResponseErrorModal>
        </>
    )
}
export default AirportCustomers;